import React, { useCallback } from 'react';
import { Node } from 'types';

import { RailStoreV2, TopAssetsStore } from '@kaltura-ott/tvpil-shared';

import MainLayout from 'components/presenters/layouts/main/MainLayout';
import NoContent from 'components/widgets/NoContent/NoContent';
import CollectionRail from 'components/widgets/Rail/CollectionRail/CollectionRail';
import ContinueWatchingRail from 'components/widgets/Rail/ContinueWatchingRail/ContinueWatchingRail';
import HeroRail from 'components/widgets/Rail/HeroRail/HeroRail';
import LogoRail from 'components/widgets/Rail/LogoRail/LogoRail';
import RecommendationRail from 'components/widgets/Rail/RecommendationRail/RecommendationRail';
import { RecordingRail } from 'components/widgets/Rail/RecordingRail';
import RentalsRail from 'components/widgets/Rail/RentalsRail/RentalsRail';
import SeriesCatchUpRail from 'components/widgets/Rail/SeriesCatchUpRail/SeriesCatchUpRail';
import TopRail from 'components/widgets/Rail/TopRail/TopRail';
import TopShelfRail from 'components/widgets/Rail/TopShelfRail/TopShelfRail';
import { BASE } from 'consts';
import { useRootStore } from 'hooks';
import {
  applyStyleHexToHSL,
  applyStyles,
  checkInstanceOf,
  checkIsCollectionRail,
  checkIsContinueWatchingRail,
  checkIsHeroRail,
  checkIsLogoRailV2,
  checkIsRecommendationRail,
  checkIsRentalsRail,
  checkIsSeriesCatchUpRail,
} from 'utils';
import { checkIsRecordingRail } from 'utils/checkIsRecordingRail';
import { checkIsTopRail } from 'utils/checkIsTopRail';

import { BaseInitHook } from './components/BaseInitHook';
import MaxiPlayerViewResolver from './components/MaxiPlayerViewResolver';

import styles from './Base.module.scss';

interface Props {
  id?: string;
}

function Base({ id }: Props) {
  const rootStore = useRootStore();
  const store = rootStore?.getInternalPageById(id);
  const { topShelf, widgets } = store?.groupedWidgets || { widgets: [], topShelf: undefined };
  let presentPlayer = false;

  if (store) {
    presentPlayer = store.presentPlayer;
  }

  const presentTopShelf = !!topShelf && !presentPlayer;
  const handleRef = useCallback(
    (node: Node) => {
      const { uiDesignElements } = store!;

      if (uiDesignElements && node) {
        applyStyles(node, uiDesignElements, false, true);
        applyStyleHexToHSL(node, uiDesignElements.backgroundColor);
      }
    },
    [id],
  );

  return (
    <MainLayout forwardRef={handleRef} pageId={store.referenceId}>
      <BaseInitHook presentPlayer={presentPlayer} store={store} />
      <div id={BASE} className={styles.base}>
        {presentPlayer && <MaxiPlayerViewResolver pageReferenceId={store.referenceId} />}
        {presentTopShelf && <TopShelfRail referenceId={topShelf.referenceId} />}

        <div className={styles.widgets}>
          {widgets.map((widget: any) => {
            if (checkInstanceOf(widget, RailStoreV2) || checkInstanceOf(widget, TopAssetsStore)) {
              // This is a temporary solution
              if (checkIsTopRail(widget)){
                return <TopRail referenceId={widget.referenceId} key={widget.id}/>
              }

              if (checkIsCollectionRail(widget)) {
                return <CollectionRail referenceId={widget.referenceId} key={widget.id} />;
              }

              if (checkIsHeroRail(widget)) {
                return (
                  <HeroRail referenceId={widget.referenceId} pageReferenceId={widget.pageReferenceId} key={widget.id} />
                );
              }

              if (checkIsSeriesCatchUpRail(widget)) {
                return <SeriesCatchUpRail referenceId={widget.referenceId} key={widget.id} />;
              }

              if (checkIsRentalsRail(widget)) {
                return <RentalsRail referenceId={widget.referenceId} key={widget.id} />;
              }

              if (checkIsRecommendationRail(widget)) {
                return <RecommendationRail referenceId={widget.referenceId} key={widget.id} />;
              }

              if (checkIsContinueWatchingRail(widget)) {
                return <ContinueWatchingRail referenceId={widget.referenceId} key={widget.id} />;
              }

              if (checkIsRecordingRail(widget)) {
                return <RecordingRail referenceId={widget.referenceId} key={widget.id} />;
              }

              if (checkIsLogoRailV2(widget.categoryTree)) {
                return <LogoRail key={widget.id} referenceId={widget.referenceId} />;
              }
            }
            return null;
          })}

        </div>
        <NoContent className={styles.noContent} title={store.name} />
      </div>
    </MainLayout>
  );
}

export default React.memo(Base);
